import { useEffect, useRef, type JSX } from 'react'
import {
  AlertCircle,
  BarChart3,
  CheckCircle2,
  Clock,
  X,
  XCircle,
} from 'lucide-react'

interface EvalResultCase {
  caseId: string
  prompt: string
  expected: string
  actual: string
  passed: boolean
  score: number
  latencyMs: number
  error?: string
  actualTools?: string[]
  missingTools?: string[]
  unexpectedTools?: string[]
  argumentErrors?: Array<{ tool: string; errors: string[] }>
}

interface EvalRun {
  id: string
  suiteId: string
  suiteName: string
  status: 'running' | 'completed' | 'failed'
  alias: string
  totalCases: number
  passedCases: number
  failedCases: number
  accuracyRate: number
  avgLatencyMs: number
  totalTokens: number
  createdAt: string
  completedAt?: string
  results: EvalResultCase[]
}

interface EvalRunDetailDrawerProps {
  run: EvalRun | null
  isOpen: boolean
  onClose: () => void
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString()
  } catch {
    return iso
  }
}

function useFocusTrap(
  ref: React.RefObject<HTMLElement | null>,
  isOpen: boolean,
  onEscape: () => void,
) {
  useEffect(() => {
    if (!isOpen) return
    const drawer = ref.current
    if (!drawer) return

    const focusables = () =>
      Array.from(
        drawer.querySelectorAll<HTMLElement>(
          'input, textarea, button, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => !el.hasAttribute('disabled'))

    const first = focusables()[0]
    first?.focus()

    function handleKeydown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault()
        onEscape()
        return
      }
      if (e.key !== 'Tab' || !drawer) return
      const list = focusables()
      if (list.length === 0) return
      const firstEl = list[0]!
      const lastEl = list[list.length - 1]!
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault()
        lastEl.focus()
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault()
        firstEl.focus()
      }
    }

    document.addEventListener('keydown', handleKeydown)
    return () => document.removeEventListener('keydown', handleKeydown)
  }, [isOpen, onEscape, ref])
}

export default function EvalRunDetailDrawer({
  run,
  isOpen,
  onClose,
}: EvalRunDetailDrawerProps): JSX.Element | null {
  const drawerRef = useRef<HTMLDivElement>(null)
  useFocusTrap(drawerRef, isOpen, onClose)

  if (!isOpen || !run) return null

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-labelledby="eval-detail-title">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity" onClick={onClose} aria-hidden="true" />

      <div
        ref={drawerRef}
        className="absolute right-0 top-0 flex h-full w-full flex-col bg-white shadow-2xl md:w-[560px]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 p-4">
          <div className="flex min-w-0 items-center gap-2">
            <BarChart3 className="h-4 w-4 shrink-0 text-gray-500" />
            <span id="eval-detail-title" className="min-w-0 truncate text-sm font-semibold text-gray-900">
              {run.suiteName}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close drawer"
            className="rounded-sm p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 space-y-5 overflow-y-auto p-4 pb-6">
          {/* Status */}
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span
              className={`rounded-sm px-1.5 py-0.5 font-medium ${
                run.status === 'completed'
                  ? 'bg-emerald-100 text-emerald-800'
                  : run.status === 'failed'
                    ? 'bg-red-100 text-red-800'
                    : 'bg-amber-100 text-amber-800'
              }`}
            >
              {run.status}
            </span>
            <span className="text-gray-300">|</span>
            <span className="text-gray-600">{run.alias}</span>
            <span className="text-gray-300">|</span>
            <span className="flex items-center gap-1 text-gray-600">
              <Clock className="h-3 w-3" />
              {formatDate(run.createdAt)}
            </span>
          </div>

          {/* Metrics */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-sm border border-gray-200 bg-white p-3 text-center shadow-sm">
              <span className="text-[10px] font-medium text-gray-500">Accuracy</span>
              <p className={`mt-1 text-xl font-bold ${run.accuracyRate >= 90 ? 'text-emerald-600' : 'text-amber-600'}`}>
                {run.accuracyRate}%
              </p>
              <span className="text-[9px] text-gray-400">{run.passedCases}/{run.totalCases} passed</span>
            </div>
            <div className="rounded-sm border border-gray-200 bg-white p-3 text-center shadow-sm">
              <span className="text-[10px] font-medium text-gray-500">Avg Latency</span>
              <p className="mt-1 text-xl font-bold text-gray-900">{run.avgLatencyMs}ms</p>
              <span className="text-[9px] text-gray-400">per turn</span>
            </div>
            <div className="rounded-sm border border-gray-200 bg-white p-3 text-center shadow-sm">
              <span className="text-[10px] font-medium text-gray-500">Tokens</span>
              <p className="mt-1 text-xl font-bold text-gray-900">{run.totalTokens.toLocaleString()}</p>
              <span className="text-[9px] text-gray-400">in/out</span>
            </div>
            <div className="rounded-sm border border-gray-200 bg-white p-3 text-center shadow-sm">
              <span className="text-[10px] font-medium text-gray-500">Failed</span>
              <p className="mt-1 text-xl font-bold text-red-600">{run.failedCases}</p>
              <span className="text-[9px] text-gray-400">cases</span>
            </div>
          </div>

          {/* Case traces */}
          <div className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Test Case Traces ({run.results.length})
            </h3>
            {run.results.length === 0 ? (
              <p className="text-sm text-gray-500">No case traces available for this run.</p>
            ) : (
              run.results.map((c) => (
                <div
                  key={c.caseId}
                  className={`rounded-sm border p-3 transition-all ${
                    c.passed ? 'border-gray-200 bg-white' : 'border-red-200 bg-red-50/30'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2">
                      {c.passed ? (
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                      ) : (
                        <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
                      )}
                      <span className="text-xs font-semibold text-gray-900">{c.caseId}</span>
                    </div>
                    <span className="rounded-sm bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] text-gray-600">
                      {c.latencyMs}ms | {(c.score * 100).toFixed(0)}%
                    </span>
                  </div>

                  <p className="mt-2 text-xs text-gray-700">
                    <span className="font-semibold text-gray-900">Prompt:</span> {c.prompt}
                  </p>

                  <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <div className="rounded-sm border border-gray-100 bg-gray-50 p-2.5">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Expected</span>
                      <p className="mt-1 text-xs text-gray-700">{c.expected}</p>
                    </div>
                    <div className="rounded-sm border border-gray-100 bg-gray-50 p-2.5">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Actual</span>
                      <p className="mt-1 text-xs text-gray-700">{c.actual}</p>
                    </div>
                  </div>

                  {(c.actualTools?.length || c.missingTools?.length || c.unexpectedTools?.length || c.argumentErrors?.length) ? (
                    <div className="mt-3 rounded-sm border border-gray-100 bg-white p-2.5">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Tool Calls</span>
                      <div className="mt-1.5 grid grid-cols-1 gap-1.5 text-[11px]">
                        {c.actualTools && c.actualTools.length > 0 && (
                          <div className="flex flex-wrap items-center gap-1">
                            <span className="font-medium text-gray-600">Actual:</span>
                            {c.actualTools.map((t) => (
                              <span key={t} className="rounded-sm bg-blue-50 px-1.5 py-0.5 text-blue-700">
                                {t}
                              </span>
                            ))}
                          </div>
                        )}
                        {c.missingTools && c.missingTools.length > 0 && (
                          <div className="flex flex-wrap items-center gap-1">
                            <span className="font-medium text-amber-700">Missing:</span>
                            {c.missingTools.map((t) => (
                              <span key={t} className="rounded-sm bg-amber-50 px-1.5 py-0.5 text-amber-700">
                                {t}
                              </span>
                            ))}
                          </div>
                        )}
                        {c.unexpectedTools && c.unexpectedTools.length > 0 && (
                          <div className="flex flex-wrap items-center gap-1">
                            <span className="font-medium text-red-700">Unexpected:</span>
                            {c.unexpectedTools.map((t) => (
                              <span key={t} className="rounded-sm bg-red-50 px-1.5 py-0.5 text-red-700">
                                {t}
                              </span>
                            ))}
                          </div>
                        )}
                        {c.argumentErrors && c.argumentErrors.length > 0 && (
                          <div className="space-y-1">
                            <span className="font-medium text-red-700">Argument validation failures:</span>
                            {c.argumentErrors.map((entry, idx) => (
                              <div key={idx} className="rounded-sm bg-red-50 p-1.5 text-[10px] text-red-800">
                                <span className="font-semibold">{entry.tool}:</span>{' '}
                                {entry.errors.join('; ')}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null}

                  {c.error && (
                    <div className="mt-2 flex items-start gap-1.5 rounded-sm bg-red-100 p-2 text-[11px] font-medium text-red-700">
                      <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      {c.error}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
